const PHOSPHOR_PREFIX = "phosphor:";
const MAKI_PREFIX = "maki:"; // Legacy support for existing persisted values
const CUSTOM_PREFIX = "custom:";

export const DEFAULT_PHOSPHOR_ICON = "map-pin";

export const PHOSPHOR_ICON_CATEGORIES = {
  "Food & Drink": ["fork-knife", "coffee", "martini", "beer-bottle", "cake"],
  Places: [
    "buildings",
    "church",
    "books",
    "projector-screen",
    "graduation-cap",
    "house",
  ],
  "Nature & Outdoors": [
    "tree",
    "mountains",
    "flower-lotus",
    "tent",
    "sun",
    "leaf",
  ],
  "Shopping & Services": [
    "shopping-bag",
    "shopping-cart",
    "first-aid-kit",
    "pill",
    "bank",
    "gas-pump",
  ],
  Transport: ["airplane", "bus", "train", "boat", "garage", "taxi", "car"],
  Activities: [
    "binoculars",
    "soccer-ball",
    "swimming-pool",
    "game-controller",
    "person-simple-hike",
  ],
  General: ["map-pin", "star", "circle", "square", "triangle"],
} as const;

export const ALL_PHOSPHOR_ICONS = Object.values(
  PHOSPHOR_ICON_CATEGORIES,
).flat();
const PHOSPHOR_ICON_SET = new Set<string>(ALL_PHOSPHOR_ICONS);

const LEGACY_MAKI_TO_PHOSPHOR: Record<string, string> = {
  marker: "map-pin",
  restaurant: "fork-knife",
  cafe: "coffee",
  bar: "martini",
  beer: "beer-bottle",
  bakery: "cake",
  museum: "buildings",
  monument: "buildings",
  castle: "house",
  theatre: "projector-screen",
  cinema: "projector-screen",
  library: "books",
  school: "graduation-cap",
  "town-hall": "buildings",
  lodging: "house",
  park: "tree",
  garden: "flower-lotus",
  beach: "sun",
  mountain: "mountains",
  volcano: "mountains",
  campsite: "tent",
  shop: "shopping-bag",
  grocery: "shopping-cart",
  pharmacy: "pill",
  hospital: "first-aid-kit",
  bank: "bank",
  fuel: "gas-pump",
  airport: "airplane",
  bus: "bus",
  rail: "train",
  ferry: "boat",
  harbor: "boat",
  parking: "garage",
  "car-rental": "car",
  attraction: "binoculars",
  stadium: "soccer-ball",
  swimming: "swimming-pool",
  golf: "person-simple-hike",
  skiing: "person-simple-hike",
  star: "star",
  circle: "circle",
  square: "square",
  triangle: "triangle",
};

export type ParsedTagIcon =
  | { kind: "custom"; dataUrl: string }
  | { kind: "phosphor"; name: string };

export function toPhosphorTagIcon(name: string): string {
  const normalized = normalizePhosphorName(name);
  return `${PHOSPHOR_PREFIX}${normalized}`;
}

export function toCustomTagIcon(dataUrl: string): string {
  return `${CUSTOM_PREFIX}${dataUrl}`;
}

export function isCustomTagIconValue(icon: string): boolean {
  return icon.startsWith(CUSTOM_PREFIX) || icon.startsWith("data:image/");
}

export function parseTagIcon(icon: string | null | undefined): ParsedTagIcon {
  const raw = (icon ?? "").trim();
  if (raw.length === 0) {
    return { kind: "phosphor", name: DEFAULT_PHOSPHOR_ICON };
  }

  if (raw.startsWith(CUSTOM_PREFIX)) {
    return { kind: "custom", dataUrl: raw.slice(CUSTOM_PREFIX.length) };
  }
  if (raw.startsWith("data:image/")) {
    return { kind: "custom", dataUrl: raw };
  }

  if (raw.startsWith(PHOSPHOR_PREFIX)) {
    return {
      kind: "phosphor",
      name: normalizePhosphorName(raw.slice(PHOSPHOR_PREFIX.length)),
    };
  }

  // Legacy "maki:<name>" values in persisted data
  if (raw.startsWith(MAKI_PREFIX)) {
    return {
      kind: "phosphor",
      name: mapLegacyMakiName(raw.slice(MAKI_PREFIX.length)),
    };
  }

  // Accept raw phosphor icon names
  if (PHOSPHOR_ICON_SET.has(raw)) {
    return { kind: "phosphor", name: raw };
  }

  // Accept raw Maki icon names as legacy input
  if (LEGACY_MAKI_TO_PHOSPHOR[raw]) {
    return { kind: "phosphor", name: LEGACY_MAKI_TO_PHOSPHOR[raw] };
  }

  return { kind: "phosphor", name: DEFAULT_PHOSPHOR_ICON };
}

export function getCustomIconDataUrl(
  icon: string | null | undefined,
): string | null {
  const parsed = parseTagIcon(icon);
  return parsed.kind === "custom" ? parsed.dataUrl : null;
}

function mapLegacyMakiName(name: string): string {
  const candidate = name.trim().replace(/-15$/, "");
  return LEGACY_MAKI_TO_PHOSPHOR[candidate] ?? DEFAULT_PHOSPHOR_ICON;
}

function normalizePhosphorName(name: string): string {
  const candidate = name.trim();
  if (PHOSPHOR_ICON_SET.has(candidate)) return candidate;
  return mapLegacyMakiName(candidate);
}
